import { NavLink, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  LayoutDashboard, 
  Store, 
  Users, 
  FileText, 
  Wallet,
  Trophy,
  Settings,
  LogOut,
  Menu,
  X,
  Gift,
  Sparkles,
  Repeat,
  GitBranch,
  ExternalLink
} from 'lucide-react';
import { useState } from 'react';

const navItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/broker' },
  { icon: GitBranch, label: 'Pipeline', path: '/broker/pipeline', highlight: true },
  { icon: Store, label: 'Deal Marketplace', path: '/broker/marketplace' },
  { icon: Repeat, label: 'Secondary Market', path: '/broker/secondary' },
  { icon: Users, label: 'My Investors', path: '/broker/investors' },
  { icon: Sparkles, label: 'Smart EDM', path: '/broker/edm' },
  { icon: FileText, label: 'Commission Ledger', path: '/broker/ledger' },
  { icon: Wallet, label: 'Wallet & Payouts', path: '/broker/wallet' },
  { icon: Trophy, label: 'Leaderboard', path: '/broker/leaderboard', comingSoon: true },
  { icon: Gift, label: 'Refer Brokers', path: '/broker/referrals' },
  { icon: Users, label: 'Team Management', path: '/broker/team', enterprise: true },
];

export default function BrokerSidebar({ activeTab, setActiveTab }) {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/broker/auth');
  };

  return (
    <>
      {/* Mobile Menu Button */}
      <button
        onClick={() => setMobileOpen(!mobileOpen)}
        className="md:hidden fixed top-4 left-4 z-50 p-2 bg-[#0A0A0A] border border-white/10 rounded-sm"
      >
        {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {/* Overlay */}
      {mobileOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black/80 z-40"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full w-64 bg-[#0A0A0A] border-r border-white/5 z-40
        transform transition-transform duration-300 ease-in-out
        ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
        md:translate-x-0
      `}>
        {/* Logo */}
        <div className="p-6 border-b border-white/5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#4ADE80] rounded-sm flex items-center justify-center">
              <span className="text-black font-bold text-lg">P</span>
            </div>
            <div>
              <h1 className="text-lg font-light tracking-tight text-white">PARTNER</h1>
              <p className="text-xs text-white/40 font-mono">BROKER PORTAL</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) => `
                  flex items-center gap-3 px-4 py-3 rounded-sm transition-all duration-200
                  ${isActive 
                    ? 'bg-[#4ADE80]/10 border border-[#4ADE80]/30 text-[#4ADE80]' 
                    : 'text-white/50 hover:text-white hover:bg-white/5 border border-transparent'
                  }
                `}
              >
                <Icon className="w-5 h-5" />
                <span className="text-sm">{item.label}</span>
                {item.highlight && (
                  <span className="ml-auto text-[10px] px-1.5 py-0.5 bg-[#4ADE80]/20 text-[#4ADE80] rounded animate-pulse">
                    NEW
                  </span>
                )}
                {item.comingSoon && (
                  <span className="ml-auto text-[10px] px-1.5 py-0.5 bg-yellow-500/20 text-yellow-400 rounded">
                    SOON
                  </span>
                )}
                {item.enterprise && (
                  <span className="ml-auto text-[10px] px-1.5 py-0.5 bg-purple-500/20 text-purple-400 rounded">
                    PRO
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* Bottom Section */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/5">
          <NavLink
            to="/broker/settings"
            className="flex items-center gap-3 px-4 py-3 text-white/50 hover:text-white transition-colors"
          >
            <Settings className="w-5 h-5" />
            <span className="text-sm">Settings</span>
          </NavLink>
          <button
            onClick={() => navigate('/')}
            className="w-full flex items-center gap-3 px-4 py-3 text-white/50 hover:text-white transition-colors"
            data-testid="exit-dashboard-btn"
          >
            <ExternalLink className="w-5 h-5" />
            <span className="text-sm">Back to Website</span>
          </button>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 text-white/50 hover:text-red-400 transition-colors"
          >
            <LogOut className="w-5 h-5" />
            <span className="text-sm">Logout</span>
          </button>
        </div>
      </aside>
    </>
  );
}